<template>
  <div class="track-transfer-page">
    <!-- global background image -->
    <div class="page-bg" aria-hidden="true"></div>
    <!-- decorative curved-line overlay -->
    <div class="bg-pattern" aria-hidden="true"></div>
    <!-- top navigation -->
    <header class="page-header d-flex justify-content-between align-items-center px-4 px-lg-5 py-3">
    </header>
    <!-- main section -->
    <div class="main-wrapper d-flex flex-column justify-content-center align-items-center text-center flex-grow-1 px-3 px-md-0">
      <img src="@/assets/images/company_logo_2.png" height="80" alt="Wiremit logo" class="mb-5" />

      <h1 class="display-5 font-weight-semibold">
        Oops!&nbsp;<span class="accent">Something Went Wrong</span>
      </h1>
      <p class="subtitle mb-5">It's us, not you.<br/> Please try refreshing the page.</p>
      <div class="track-card p-4 p-md-5 shadow-sm">
        <div class="text-center">
          <!-- Error Icon -->
          <div class="mb-4">
            <img src="@/assets/images/failed.gif" alt="Error" style="width: 80px; height: 80px; object-fit: contain;" />
          </div>

          <!-- Main Message -->
          <div class="mb-4">
            <h4 class="text-muted mb-3">
              We're sorry, but something unexpected happened. Our team has been notified and we're working to fix this issue.
            </h4>
          </div>

          <!-- Error Details (only in development) -->
          <div class="mb-4" v-if="showErrorDetails && errorMessage">
            <div class="alert alert-warning">
              <i class="mdi mdi-bug mr-2"></i>
              <strong>Error Details:</strong> {{ errorMessage }}
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button @click="reloadPage" class="btn btn-primary w-md mb-3">
              <i class="mdi mdi-refresh mr-2"></i>Try Again
            </button>
            
            <router-link to="/" class="btn btn-outline-primary w-md mb-3 ml-md-3">
              <i class="mdi mdi-home mr-2"></i>Go to Home Page
            </router-link>
            
            <div class="mt-3">
              <button @click="reportError" class="btn btn-link font-size-14 text-muted">
                <i class="mdi mdi-bug mr-1"></i> Report this issue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authStorageService from "@/services/auth/auth-storage.service";

export default {
  name: 'ErrorBoundary',
  props: {
    error: {
      type: [Error, String, Object],
      default: null
    },
    errorInfo: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      user: this.getUserFromStorage(),
    };
  },
  computed: {
    showErrorDetails() {
      return process.env.NODE_ENV === 'development';
    },
    errorMessage() {
      if (!this.error) return '';
      
      if (typeof this.error === 'string') {
        return this.error;
      }
      
      if (this.error.message) {
        return this.error.message;
      }
      
      return 'Unknown error occurred';
    }
  },
  created() {
    document.body.classList.add("auth-body-bg");
    
    // Log error for debugging
    if (this.error) {
      console.error('ErrorBoundary caught error:', this.error, this.errorInfo);
      
      // Report error to monitoring service (if available)
      this.logErrorToService();
    }
  },
  beforeDestroy() {
    document.body.classList.remove("auth-body-bg");
  },
  methods: {
    getUserFromStorage() {
      try {
        return authStorageService.getUser();
      } catch (e) {
        console.warn('Failed to parse user data from storage:', e);
        return null;
      }
    },
    
    reloadPage() {
      window.location.reload();
    },
    
    reportError() {
      // You can implement error reporting logic here
      // For now, just show an alert
      this.$swal({
        title: 'Thank you!',
        text: 'Your error report has been submitted. We\'ll look into this issue.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
    },
    
    logErrorToService() {
      // Implement error logging to your monitoring service
      // Example: Sentry, LogRocket, etc.
      try {
        // If you have a monitoring service, log the error here
        console.log('Logging error to monitoring service:', {
          error: this.error,
          errorInfo: this.errorInfo,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
          userId: this.user?.id
        });
      } catch (e) {
        console.error('Failed to log error to monitoring service:', e);
      }
    }
  }
};
</script>

<style scoped>
/* Make the track-card relative for proper positioning */
.track-card {
  position: relative;
  max-width: 500px;
}

/* Error icon color - using danger color instead of warning */
.text-danger {
  color: #dc3545 !important;
}

/* Alert styling improvements - matching unauthorised page exactly */
.alert {
  border: none;
  border-radius: 8px;
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

/* Action buttons styling - matching unauthorised page */
.action-buttons .btn {
  min-width: 200px;
}

.action-buttons .btn-link {
  min-width: auto;
  text-decoration: none;
}

.action-buttons .btn-link:hover {
  text-decoration: underline;
}

/* Typography improvements - matching unauthorised page */
h4 {
  line-height: 1.5;
}

/* Border styling - matching unauthorised page */
.border-top {
  border-top: 1px solid #e9ecef !important;
}

/* Responsive adjustments - matching unauthorised page */
@media (max-width: 768px) {
  .track-card {
    margin: 0 1rem;
  }
  
  .action-buttons .btn {
    min-width: 100%;
    margin-bottom: 0.5rem;
  }
  
  .action-buttons .btn.ml-md-3 {
    margin-left: 0 !important;
  }
}
</style>
